import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { api } from '../services/api';
import { fetchEdgeHealth } from '../services/opsCopilot';
import { useMode } from '../contexts/ModeContext';

type FirmwareResponse = {
  ok?: boolean;
  device_id?: string;
  ip_address?: string;
  image_build?: any;
  edgentik_firmware?: { data?: any; present?: boolean; path?: string };
  error?: string;
  detail?: string;
};

type PreflightLatestResponse = {
  ok?: boolean;
  device_id?: string;
  ip_address?: string;
  ran_at?: string;
  duration_ms?: number;
  exit_code?: number;
  preflight_passed?: boolean;
  results?: any;
  error?: string;
  detail?: string;
};

type CommissioningArtifactsResponse = {
  ok?: boolean;
  device_id?: string;
  ip_address?: string;
  commissioning_readiness?: {
    ok?: boolean;
    filename?: string;
    mtime?: string;
    path?: string;
    report?: {
      schema_version?: string;
      contract_version?: string;
      generated_at?: string;
      decision?: string;
      failed_blockers?: string[];
      failed_blockers_warn?: string[];
      blocker_summary?: {
        failed_error?: number;
        failed_warn?: number;
        passed?: number;
        total?: number;
      };
      blockers?: Record<string, any>;
    };
  };
  error?: string;
  detail?: string;
};

type CommissioningRunResponse = {
  ok?: boolean;
  device_id?: string;
  ip_address?: string;
  decision?: string;
  contract_version?: string;
  failed_blockers?: string[];
  failed_blockers_warn?: string[];
  blocker_summary?: any;
  error?: string;
  detail?: string;
};

type EdgeHealthResponse = {
  ok?: boolean;
  status?: string;
  timestamp?: string;
  device_id?: string;
  ip_address?: string;
  clock?: {
    ok?: boolean;
    ntp_enabled?: boolean | null;
    ntp_synchronized?: boolean | null;
    timezone?: string | null;
    source?: string | null;
    clock_unsynced?: boolean | null;
    checked_at?: string;
    error?: string;
    detail?: string;
  };
  error?: string;
  detail?: string;
};

type PreflightCategory = {
  name: string;
  checks: { check: string; status: string; detail?: string }[];
};

function pretty(v: any): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function statusChip(label: string, color: 'success' | 'warning' | 'error' | 'default') {
  return <Chip size="small" label={label} color={color} />;
}

function formatDuration(ms: number | null | undefined): string {
  if (!Number.isFinite(ms as number)) return '—';
  const total = Math.max(0, Math.round(Number(ms)));
  const s = Math.floor(total / 1000);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  if (mm <= 0) return `${ss}s`;
  return `${mm}m ${ss}s`;
}

export const FirmwareReadinessPanel: React.FC<{ deviceId: string; ipAddress?: string }> = ({
  deviceId,
  ipAddress,
}) => {
  const { mode } = useMode();
  const [firmware, setFirmware] = useState<FirmwareResponse | null>(null);
  const [preflight, setPreflight] = useState<PreflightLatestResponse | null>(null);
  const [edgeHealth, setEdgeHealth] = useState<EdgeHealthResponse | null>(null);
  const [commissioningArtifacts, setCommissioningArtifacts] = useState<CommissioningArtifactsResponse | null>(null);
  const [commissioningRun, setCommissioningRun] = useState<CommissioningRunResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [, setSupplementalLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [gateRunning, setGateRunning] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<any | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updatePollNote, setUpdatePollNote] = useState<string | null>(null);
  const [updateClientStartMs, setUpdateClientStartMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requireRtsp, setRequireRtsp] = useState(false);
  const [verifyAfterUpdate, setVerifyAfterUpdate] = useState(true);
  const [toast, setToast] = useState<{ open: boolean; severity: 'success' | 'info' | 'warning' | 'error'; message: string }>({
    open: false,
    severity: 'info',
    message: '',
  });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [jumpTo, setJumpTo] = useState<string>('');
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
  const [updateComponents, setUpdateComponents] = useState<'both' | 'native' | 'docker'>('both');
  const [breakGlassUpdateEnabled, setBreakGlassUpdateEnabled] = useState(false);

  const edgeIp = firmware?.ip_address || ipAddress;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCommissioningRun(null);
    const params: any = {};
    if (ipAddress) params.edge_host = ipAddress;

    const loadSlice = async (
      requestPromise: Promise<any>,
      onSuccess: (data: any) => void,
    ): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await requestPromise;
        onSuccess(res.data);
        return { ok: true };
      } catch (e: any) {
        return { ok: false, error: e?.response?.data?.error || e?.message || 'request_failed' };
      }
    };

    try {
      const [fw, pf, up] = await Promise.all([
        loadSlice(api.get(`/api/v1/pipeline/devices/${deviceId}/edge/firmware`, { params }), setFirmware),
        loadSlice(api.get(`/api/v1/pipeline/devices/${deviceId}/edge/preflight/latest`, { params }), setPreflight),
        loadSlice(api.get(`/api/v1/pipeline/devices/${deviceId}/edge/update/latest`), setUpdateStatus),
      ]);
      const coreErrors = [fw, pf].filter((result) => !result.ok);
      const allFailed = [fw, pf, up].every((result) => !result.ok);
      if (allFailed) {
        setError(coreErrors[0]?.error || up.error || 'Failed to load firmware/preflight');
      } else if (coreErrors.length === 2) {
        setError(coreErrors[0]?.error || coreErrors[1]?.error || 'Failed to load firmware/preflight');
      }

      setSupplementalLoading(true);
      void Promise.all([
        fetchEdgeHealth(deviceId, ipAddress ? { edgeHost: ipAddress } : undefined)
          .then((data) => {
            setEdgeHealth(data);
          })
          .catch(() => {
            setEdgeHealth(null);
          }),
        loadSlice(
          api.get(`/api/v1/pipeline/devices/${deviceId}/edge/commissioning/artifacts`, { params }),
          setCommissioningArtifacts,
        ),
      ]).finally(() => {
        setSupplementalLoading(false);
      });
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load firmware/preflight');
    } finally {
      setLoading(false);
    }
  }, [deviceId, ipAddress]);

  const pollUpdateOnce = useCallback(async () => {
    const st = await api.get(`/api/v1/pipeline/devices/${deviceId}/edge/update/latest`);
    setUpdateStatus(st.data);
    return st.data;
  }, [deviceId]);

  const isUpdateRunning = useMemo(() => String(updateStatus?.status || '').toLowerCase() === 'running', [updateStatus?.status]);

  const updateServerDurationMs = useMemo(() => {
    const started = String(updateStatus?.started_at || '').trim();
    const finished = String(updateStatus?.finished_at || '').trim();
    const a = started ? Date.parse(started) : NaN;
    const b = finished ? Date.parse(finished) : NaN;
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return Math.max(0, b - a);
  }, [updateStatus?.started_at, updateStatus?.finished_at]);

  const updateClientElapsedMs = useMemo(() => {
    if (!updateClientStartMs) return null;
    const status = String(updateStatus?.status || '').toLowerCase();
    const isTerminal = status === 'ok' || status === 'ok_with_warnings' || status === 'error';
    if (!isTerminal) return Math.max(0, Date.now() - updateClientStartMs);
    const finished = String(updateStatus?.finished_at || '').trim();
    const b = finished ? Date.parse(finished) : NaN;
    if (Number.isFinite(b)) return Math.max(0, b - updateClientStartMs);
    return Math.max(0, Date.now() - updateClientStartMs);
  }, [updateClientStartMs, updateStatus?.status, updateStatus?.finished_at]);

  const updateProgress = useMemo(() => {
    const steps: any[] = Array.isArray(updateStatus?.steps) ? updateStatus.steps : [];
    const status = String(updateStatus?.status || '').toLowerCase();
    const components: string[] = Array.isArray(updateStatus?.components) ? updateStatus.components : [];

    // Deploy-runner currently reports one step per component:
    // - native -> deploy_device
    // - docker -> deploy_edge
    // Older edge-side update flows may report different step counts; fall back gracefully.
    const expectedByComponents = components.length || 0;

    // While running: show progress vs expected components if known, otherwise vs observed steps.
    // When terminal (ok/error): progress is complete based on observed steps.
    const total = status === 'running' ? Math.max(steps.length, expectedByComponents, 1) : Math.max(steps.length, 1);
    const terminalSuccess = status === 'ok' || status === 'ok_with_warnings';
    const done = terminalSuccess ? total : Math.min(steps.length, total);
    const percent = terminalSuccess ? 100 : total ? Math.round((done / total) * 100) : 0;
    return { steps, total, done, percent };
  }, [updateStatus]);

  const updateChip = useMemo(() => {
    const s = String(updateStatus?.status || '').toLowerCase();
    if (!s) return null;
    if (s === 'running') return statusChip('UPDATING', 'warning');
    if (s === 'ok') return statusChip('UPDATED', 'success');
    if (s === 'ok_with_warnings') return statusChip('UPDATED (WARN)', 'warning');
    if (s === 'error') return statusChip('UPDATE FAILED', 'error');
    return statusChip(String(s).toUpperCase(), 'default');
  }, [updateStatus]);

  const strictModeChip = useMemo(() => {
    const strict = !!updateStatus?.strict_mode;
    return strict ? statusChip('STRICT MODE', 'warning') : null;
  }, [updateStatus?.strict_mode]);

  const clockChip = useMemo(() => {
    const unsynced = edgeHealth?.clock?.clock_unsynced;
    if (unsynced === true) return statusChip('CLOCK UNSYNCED', 'warning');
    if (unsynced === false) return statusChip('CLOCK SYNCED', 'success');
    return statusChip('CLOCK UNKNOWN', 'default');
  }, [edgeHealth?.clock?.clock_unsynced]);

  const updateWarnings = useMemo(() => {
    const w = (updateStatus as any)?.warnings;
    return Array.isArray(w) ? w : [];
  }, [updateStatus]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const runPreflight = useCallback(async () => {
    setRunning(true);
    setError(null);
    setToast({ open: true, severity: 'info', message: 'Running preflight on edge…' });
    try {
      const payload: any = {
        cameras: true,
        require_rtsp_reachable: requireRtsp,
        timeout_sec: 140,
        // Warehouse demo: ignore disabled OCR test camera(s) so edge preflight stays focused on warehouse cameras.
        ...(mode === 'warehouse' ? { exclude_camera_regex: '^(cam_loading_01)$' } : {}),
      };
      if (edgeIp) payload.edge_host = edgeIp;
      const resp = await api.post(`/api/v1/pipeline/devices/${deviceId}/edge/preflight/run`, payload);
      setPreflight(resp.data);
      const exit = Number(resp.data?.exit_code);
      if (resp.data?.ok === false) {
        setToast({
          open: true,
          severity: 'error',
          message: `Preflight failed to run: ${resp.data?.error || 'unknown error'}`,
        });
      } else if (Number.isFinite(exit) && exit === 0) {
        setToast({ open: true, severity: 'success', message: 'Preflight PASSED (Ready).' });
      } else {
        setToast({
          open: true,
          severity: requireRtsp ? 'error' : 'warning',
          message: `Preflight completed with issues (exit ${Number.isFinite(exit) ? exit : 'unknown'}).`,
        });
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to run preflight');
      setToast({ open: true, severity: 'error', message: 'Preflight request failed (edge unreachable or error).' });
    } finally {
      setRunning(false);
    }
  }, [deviceId, requireRtsp, mode, edgeIp]);

  const runGate = useCallback(async () => {
    setGateRunning(true);
    setError(null);
    setToast({ open: true, severity: 'info', message: 'Running commissioning gate on edge…' });
    try {
      const payload: any = {};
      if (edgeIp) payload.edge_host = edgeIp;
      const resp = await api.post(`/api/v1/pipeline/devices/${deviceId}/edge/commissioning/run`, payload, { timeout: 120000 } as any);
      setCommissioningRun(resp.data || null);
      if (resp.data?.ok === false) {
        setToast({
          open: true,
          severity: 'error',
          message: `Gate failed to run: ${resp.data?.error || 'unknown error'}`,
        });
      } else {
        const d = String(resp.data?.decision || '').toLowerCase();
        if (d === 'pass') setToast({ open: true, severity: 'success', message: 'Commissioning gate PASSED.' });
        else if (d === 'fail') setToast({ open: true, severity: 'error', message: 'Commissioning gate FAILED.' });
        else setToast({ open: true, severity: 'warning', message: `Gate completed: decision=${resp.data?.decision || 'unknown'}` });
      }
      // Refresh canonical artifacts (source of truth for the UI).
      try {
        const params: any = {};
        if (ipAddress) params.edge_host = ipAddress;
        const art = await api.get(`/api/v1/pipeline/devices/${deviceId}/edge/commissioning/artifacts`, { params });
        setCommissioningArtifacts(art.data);
      } catch {
        // ignore
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to run commissioning gate');
      setToast({ open: true, severity: 'error', message: 'Gate request failed (edge unreachable or error).' });
    } finally {
      setGateRunning(false);
    }
  }, [deviceId, edgeIp, ipAddress]);

  const triggerUpdate = useCallback(async () => {
    setConfirmUpdateOpen(false);
    setUpdateDialogOpen(true);
    setUpdating(true);
    setError(null);
    setUpdatePollNote(null);
    const clientStart = Date.now();
    setUpdateClientStartMs(clientStart);
    setToast({
      open: true,
      severity: 'info',
      message: verifyAfterUpdate ? 'Updating edge software (then verifying)…' : 'Updating edge software…',
    });
    try {
      const components =
        updateComponents === 'native' ? ['native'] : updateComponents === 'docker' ? ['docker'] : ['native', 'docker'];
      const payload: any = { components };
      if (ipAddress) payload.edge_host = ipAddress;
      const resp = await api.post(`/api/v1/pipeline/devices/${deviceId}/edge/update/edgentik`, payload);
      setUpdateStatus(resp.data);
      if (resp?.data?.job_id) {
        try {
          sessionStorage.setItem(`edgentik:update_start:${resp.data.job_id}`, String(clientStart));
        } catch {
          // ignore
        }
      }
      // Poll for completion (edge will restart services)
      const startedAt = Date.now();
      let lastStatus: any = resp.data;
      while (Date.now() - startedAt < 180_000) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const stData = await pollUpdateOnce();
          lastStatus = stData;
          const s = String(stData?.status || '').toLowerCase();
          if (s && s !== 'running') break;
          if (updatePollNote) setUpdatePollNote(null);
        } catch {
          // Edge may be restarting (or cloud proxy can't reach it momentarily); keep polling.
          setUpdatePollNote('Edge restarting / temporarily unreachable. Still waiting for update to complete…');
        }
      }
      const final = lastStatus;
      const finalStatus = String(final?.status || '').toLowerCase();
      if (finalStatus === 'ok' || finalStatus === 'ok_with_warnings') {
        setToast({ open: true, severity: 'success', message: 'Update completed successfully.' });
        await fetchAll();
        if (verifyAfterUpdate) {
          try {
            await runGate();
          } catch {
            // runGate already toasts; ignore here
          }
        }
      } else if (finalStatus === 'error') {
        setToast({ open: true, severity: 'error', message: 'Update failed. See details in the update dialog.' });
      } else {
        setToast({ open: true, severity: 'warning', message: 'Update is still running (or status unknown). Open Update details.' });
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to update Edgentik');
      setToast({ open: true, severity: 'error', message: 'Update failed (see error details).' });
    } finally {
      setUpdating(false);
    }
  }, [
    deviceId,
    fetchAll,
    pollUpdateOnce,
    updatePollNote,
    updateComponents,
    ipAddress,
    runGate,
    verifyAfterUpdate,
  ]);

  const cancelUpdate = useCallback(async () => {
    setUpdatePollNote('Cancelling update…');
    try {
      const resp = await api.post(`/api/v1/pipeline/devices/${deviceId}/edge/update/cancel`, {});
      try {
        await pollUpdateOnce();
      } catch {
        // ignore
      }
      if (resp?.data?.ok) {
        setToast({ open: true, severity: 'success', message: 'Update cancelled.' });
      } else {
        setToast({ open: true, severity: 'warning', message: `Cancel not completed: ${resp?.data?.error || 'unknown'}` });
      }
    } catch {
      setToast({ open: true, severity: 'error', message: 'Cancel request failed.' });
    } finally {
      setUpdatePollNote(null);
    }
  }, [deviceId, pollUpdateOnce]);

  const openUpdateLogs = useCallback(async () => {
    try {
      const resp = await api.get(`/api/v1/pipeline/devices/${deviceId}/edge/update/logs`, { responseType: 'text' as any });
      const txt = String((resp as any)?.data || '');
      const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setToast({ open: true, severity: 'error', message: 'Unable to fetch update logs.' });
    }
  }, [deviceId]);

  const rollbackNative = useCallback(async () => {
    setUpdatePollNote('Rolling back native…');
    try {
      const resp = await api.post(`/api/v1/pipeline/devices/${deviceId}/edge/update/rollback/native`, { dry_run: false });
      if (resp?.data?.ok) setToast({ open: true, severity: 'success', message: 'Native rollback started.' });
      else setToast({ open: true, severity: 'error', message: `Native rollback failed to start: ${resp?.data?.error || 'unknown'}` });
      await pollUpdateOnce();
    } catch {
      setToast({ open: true, severity: 'error', message: 'Native rollback request failed.' });
    } finally {
      setUpdatePollNote(null);
    }
  }, [deviceId, pollUpdateOnce]);

  const rollbackDocker = useCallback(async () => {
    setUpdatePollNote('Rolling back docker…');
    try {
      const resp = await api.post(`/api/v1/pipeline/devices/${deviceId}/edge/update/rollback/docker`, { dry_run: false, prefer: 'prev' });
      if (resp?.data?.ok) setToast({ open: true, severity: 'success', message: 'Docker rollback started.' });
      else setToast({ open: true, severity: 'error', message: `Docker rollback failed to start: ${resp?.data?.error || 'unknown'}` });
      await pollUpdateOnce();
    } catch {
      setToast({ open: true, severity: 'error', message: 'Docker rollback request failed.' });
    } finally {
      setUpdatePollNote(null);
    }
  }, [deviceId, pollUpdateOnce]);

  // Restore browser start timestamp (for accurate end-to-end timing) after refresh/reopen.
  useEffect(() => {
    if (updateClientStartMs) return;
    const jobId = String(updateStatus?.job_id || '').trim();
    if (!jobId) return;
    try {
      const raw = sessionStorage.getItem(`edgentik:update_start:${jobId}`);
      const n = raw ? Number(raw) : NaN;
      if (Number.isFinite(n) && n > 0) setUpdateClientStartMs(n);
    } catch {
      // ignore
    }
  }, [updateClientStartMs, updateStatus?.job_id]);

  const readiness = useMemo(() => {
    if (!preflight || preflight.ok === false) return { label: 'UNKNOWN', color: 'default' as const };
    if (preflight.exit_code === 0) return { label: 'READY', color: 'success' as const };
    // Non-zero exit code means preflight found failures (or strict warnings)
    return { label: 'NOT READY', color: 'error' as const };
  }, [preflight]);

  const gate = useMemo(() => {
    const rep = commissioningArtifacts?.commissioning_readiness?.report;
    const d = String(rep?.decision || '').toLowerCase();
    if (!rep || !d) return { label: 'GATE: UNKNOWN', color: 'default' as const, decision: null as string | null };
    if (d === 'pass') return { label: 'GATE: PASS', color: 'success' as const, decision: 'pass' };
    if (d === 'fail') return { label: 'GATE: FAIL', color: 'error' as const, decision: 'fail' };
    return { label: `GATE: ${String(rep?.decision || 'unknown').toUpperCase()}`, color: 'default' as const, decision: d };
  }, [commissioningArtifacts]);

  const fwNative = firmware?.edgentik_firmware?.data?.edgentik_firmware?.native;
  const fwDocker = firmware?.edgentik_firmware?.data?.edgentik_firmware?.docker;
  const imageBuild = firmware?.image_build;

  const categories: PreflightCategory[] = useMemo(() => {
    const r = preflight?.results;
    if (!Array.isArray(r)) return [];
    return r
      .filter((x: any) => x && typeof x === 'object' && typeof x.name === 'string')
      .map((x: any) => ({
        name: x.name,
        checks: Array.isArray(x.checks) ? x.checks : [],
      }));
  }, [preflight]);

  const statusBucket = useCallback((status: string): 'pass' | 'warn' | 'fail' | 'skip' => {
    const s = String(status || '').toLowerCase();
    if (s === 'pass' || s === 'ok' || s === 'success') return 'pass';
    if (s === 'warn' || s === 'warning') return 'warn';
    if (s === 'skip' || s === 'skipped') return 'skip';
    return 'fail';
  }, []);

  useEffect(() => {
    if (!jumpTo) return;
    setExpanded((prev) => ({ ...prev, [jumpTo]: true }));
    setTimeout(() => {
      const el = document.getElementById(`preflight-cat-${jumpTo}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, [jumpTo]);

  const categorySummary = useMemo(() => {
    const map: Record<string, { pass: number; warn: number; fail: number; skip: number }> = {};
    for (const c of categories) {
      let pass = 0;
      let warn = 0;
      let fail = 0;
      let skip = 0;
      for (const chk of c.checks || []) {
        const bucket = statusBucket(String(chk?.status || ''));
        if (bucket === 'pass') pass += 1;
        else if (bucket === 'warn') warn += 1;
        else if (bucket === 'skip') skip += 1;
        else fail += 1;
      }
      map[c.name] = { pass, warn, fail, skip };
    }
    return map;
  }, [categories, statusBucket]);

  const totals = useMemo(() => {
    let pass = 0;
    let warn = 0;
    let fail = 0;
    let skip = 0;
    for (const c of categories) {
      for (const chk of c.checks || []) {
        const bucket = statusBucket(String(chk?.status || ''));
        if (bucket === 'pass') pass += 1;
        else if (bucket === 'warn') warn += 1;
        else if (bucket === 'skip') skip += 1;
        else fail += 1;
      }
    }
    return { pass, warn, fail, skip };
  }, [categories, statusBucket]);

  const openFirstIssue = useCallback(
    (kind: 'fail' | 'warn') => {
      if (!categories.length) return;
      // Expand all categories that have the requested kind, and scroll to the first.
      const matches = categories.filter((c) => {
        const s = categorySummary[c.name];
        if (!s) return false;
        return kind === 'fail' ? s.fail > 0 : s.warn > 0;
      });
      if (matches.length === 0) return;
      const next: Record<string, boolean> = {};
      for (const m of matches) next[m.name] = true;
      setExpanded((prev) => ({ ...prev, ...next }));
      // Scroll to first match
      const first = matches[0]?.name;
      if (first) {
        setTimeout(() => {
          const el = document.getElementById(`preflight-cat-${first}`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    },
    [categories, categorySummary]
  );

  const statusIcon = (status: string) => {
    if (running) return <HourglassBottomIcon fontSize="small" color="disabled" />;
    const bucket = statusBucket(status);
    if (bucket === 'pass') return <CheckCircleIcon fontSize="small" color="success" />;
    if (bucket === 'warn') return <WarningAmberIcon fontSize="small" color="warning" />;
    if (bucket === 'skip') return <RemoveCircleOutlineIcon fontSize="small" color="disabled" />;
    return <ErrorIcon fontSize="small" color="error" />;
  };

  const showInitialSpinner =
    loading && !firmware && !preflight && !edgeHealth && !commissioningArtifacts && !updateStatus;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="h6">Firmware & Readiness</Typography>
          <Stack direction="row" gap={1} alignItems="center">
            {statusChip(readiness.label, readiness.color)}
            {statusChip(gate.label, gate.color)}
            {updateChip}
            {clockChip}
            <Button size="small" startIcon={<RefreshIcon />} onClick={fetchAll} disabled={loading}>
              Refresh
            </Button>
            <Button size="small" startIcon={<PlayArrowIcon />} onClick={runPreflight} disabled={running}>
              Run Preflight
            </Button>
            <Button
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={runGate}
              disabled={gateRunning}
            >
              Run Gate
            </Button>
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => setConfirmUpdateOpen(true)}
              disabled={updating}
            >
              Break-glass update
            </Button>
          </Stack>
        </Stack>

        <Box mt={2}>
          <Alert severity="info">
            For pilot, prefer <strong>agent-driven OTA</strong> (set <strong>desired_release</strong> and trigger agent poll) from Device Control.
            “Break-glass update” uses the legacy push update path and is intended for recovery only.
          </Alert>
        </Box>

        {error && (
          <Box mt={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {updateWarnings.length > 0 && (
          <Box mt={2}>
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Update completed with warnings ({updateWarnings.length})
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {updateWarnings.slice(0, 6).map((w, idx) => (
                  <li key={`${String(w?.kind || 'warn')}-${idx}`}>
                    <Typography variant="body2">
                      <strong>{String(w?.kind || 'warning')}</strong>
                      {w?.step ? ` • step=${String(w.step)}` : ''}
                      {w?.detail ? ` — ${String(w.detail)}` : ''}
                    </Typography>
                  </li>
                ))}
              </Box>
              {updateWarnings.length > 6 && (
                <Typography variant="caption" color="text.secondary">
                  Showing first 6 warnings. See “Raw update status (JSON)” for full list.
                </Typography>
              )}
            </Alert>
          </Box>
        )}

        {running && (
          <Box mt={2}>
            <LinearProgress />
          </Box>
        )}
        {gateRunning && (
          <Box mt={2}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Commissioning gate is running. This evaluates readiness + runtime identity and persists evidence on the edge.
            </Alert>
            <LinearProgress />
          </Box>
        )}
        {isUpdateRunning && (
          <Box mt={2}>
            <Alert severity="warning" sx={{ mb: 1 }}>
              Edgentik update in progress. You can open details to see step-by-step output.
              <Button size="small" sx={{ ml: 1 }} onClick={() => setUpdateDialogOpen(true)}>
                View update details
              </Button>
            </Alert>
            <LinearProgress />
          </Box>
        )}

        {loading && !showInitialSpinner && (
          <Box mt={2}>
            <LinearProgress />
          </Box>
        )}

        {showInitialSpinner ? (
          <Box mt={2} display="flex" justifyContent="center">
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Box mt={2}>
            <Stack direction="row" gap={3} flexWrap="wrap">
              <Box minWidth={320} flex={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Image build (device)
                </Typography>
                <Typography variant="body2">
                  <strong>model</strong>: {imageBuild?.model || 'unknown'}
                </Typography>
                <Typography variant="body2">
                  <strong>kernel</strong>: {imageBuild?.kernel || 'unknown'}
                </Typography>
                <Typography variant="body2">
                  <strong>os</strong>: {imageBuild?.os_release?.PRETTY_NAME || imageBuild?.os_release?.VERSION || 'unknown'}
                </Typography>
              </Box>

              <Box minWidth={320} flex={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Edgentik firmware (edge)
                </Typography>
                <Typography variant="body2">
                  <strong>native</strong>: {fwNative?.git_sha || 'unknown'} ({fwNative?.deployed_at || 'n/a'})
                </Typography>
                <Typography variant="body2">
                  <strong>docker</strong>: {fwDocker?.image_ref || 'unknown'} ({fwDocker?.source_git_sha || 'n/a'})
                </Typography>
                <Typography variant="body2">
                  <strong>edge ip</strong>: {edgeIp || 'unknown'}
                </Typography>
              </Box>
            </Stack>

            <Box mt={2}>
              <Divider />
            </Box>

            <Box mt={2}>
              <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                <FormControlLabel
                  control={<Switch checked={requireRtsp} onChange={(e) => setRequireRtsp(e.target.checked)} />}
                  label="Require RTSP reachable (fail if offsite/unplugged)"
                />
                <Typography variant="body2" color="text.secondary">
                  Last preflight: {preflight?.ran_at || 'never'} {preflight?.duration_ms ? `(${preflight.duration_ms}ms)` : ''}
                </Typography>
              </Stack>
            </Box>

            <Box mt={2}>
              <Typography variant="subtitle2">Preflight checklist</Typography>
              {categories.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No preflight results yet. Click <strong>Run Preflight</strong>.
                </Typography>
              ) : (
                <>
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel>Jump to category</InputLabel>
                      <Select
                        value={jumpTo}
                        label="Jump to category"
                        onChange={(e) => setJumpTo(String(e.target.value))}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {categories.map((c) => (
                          <MenuItem key={c.name} value={c.name}>
                            {String(c.name).toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1, mb: 1 }}>
                    <Chip size="small" label={`passed ${totals.pass}`} color="success" />
                    <Chip
                      size="small"
                      label={`warnings ${totals.warn}`}
                      color="warning"
                      clickable={totals.warn > 0}
                      onClick={() => totals.warn > 0 && openFirstIssue('warn')}
                    />
                    <Chip
                      size="small"
                      label={`failed ${totals.fail}`}
                      color={totals.fail > 0 ? 'error' : 'default'}
                      clickable={totals.fail > 0}
                      onClick={() => totals.fail > 0 && openFirstIssue('fail')}
                    />
                    <Chip size="small" label={`skipped ${totals.skip}`} color="default" />
                  </Stack>

                  {categories.map((c) => (
                    <Accordion
                      key={c.name}
                      id={`preflight-cat-${c.name}`}
                      sx={{ mb: 1 }}
                      expanded={!!expanded[c.name]}
                      onChange={(_, isExpanded) =>
                        setExpanded((prev) => ({
                          ...prev,
                          [c.name]: isExpanded,
                        }))
                      }
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }}>
                          {c.name}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense disablePadding>
                          {(c.checks || []).map((chk) => (
                            <ListItem key={`${c.name}:${chk.check}`} sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>{statusIcon(chk.status)}</ListItemIcon>
                              <ListItemText
                                primary={chk.check}
                                secondary={chk.detail || ''}
                                secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </>
              )}
            </Box>

            <Accordion sx={{ mt: 1 }} defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Details (raw JSON)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="subtitle2" color="text.secondary">
                  Firmware
                </Typography>
                <TextField fullWidth multiline minRows={6} value={pretty(firmware)} InputProps={{ readOnly: true }} />
                <Box mt={2} />
                <Typography variant="subtitle2" color="text.secondary">
                  Preflight
                </Typography>
                <TextField fullWidth multiline minRows={10} value={pretty(preflight)} InputProps={{ readOnly: true }} />
                <Box mt={2} />
                <Typography variant="subtitle2" color="text.secondary">
                  Commissioning gate (latest artifacts)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={8}
                  value={pretty(commissioningArtifacts)}
                  InputProps={{ readOnly: true }}
                />
                {commissioningRun && (
                  <>
                    <Box mt={2} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Commissioning gate (last run response)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={6}
                      value={pretty(commissioningRun)}
                      InputProps={{ readOnly: true }}
                    />
                  </>
                )}
                <Box mt={2} />
                <Typography variant="subtitle2" color="text.secondary">
                  Update status
                </Typography>
                <TextField fullWidth multiline minRows={6} value={pretty(updateStatus)} InputProps={{ readOnly: true }} />
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </CardContent>

      <Dialog open={confirmUpdateOpen} onClose={() => setConfirmUpdateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Break-glass update</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Break-glass only. This uses the legacy push update flow and restarts edge services (brief interruption).
            For routine pilot updates, use agent-driven OTA from Device Control (desired_release + poll now).
          </Alert>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={breakGlassUpdateEnabled}
                  onChange={(_, checked) => setBreakGlassUpdateEnabled(checked)}
                />
              }
              label="Enable break-glass update"
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={<Switch checked={verifyAfterUpdate} onChange={(_, checked) => setVerifyAfterUpdate(checked)} />}
              label="Verify after update (recommended)"
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose what to update (default: <strong>Both</strong>).
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="update-components-label">Update components</InputLabel>
              <Select
                labelId="update-components-label"
                value={updateComponents}
                label="Update components"
                onChange={(e) => setUpdateComponents(e.target.value as any)}
              >
                <MenuItem value="both">Both (native + docker)</MenuItem>
                <MenuItem value="native">Native only</MenuItem>
                <MenuItem value="docker">Docker only</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Review ring persistence now comes from desired state in Device Control &gt; Settings.
            Break-glass update only applies the software change.
          </Alert>
          <Typography variant="body2">
            Device: <strong>{deviceId}</strong>
            {edgeIp ? (
              <>
                {' '}
                • edge IP: <strong>{edgeIp}</strong>
              </>
            ) : null}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUpdateOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button onClick={triggerUpdate} variant="contained" disabled={updating || !breakGlassUpdateEnabled}>
            {updating ? 'Updating…' : verifyAfterUpdate ? 'Break-glass update + Verify' : 'Break-glass update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Update progress</DialogTitle>
        <DialogContent>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
            {updateChip || statusChip('UNKNOWN', 'default')}
            {strictModeChip}
            <Typography variant="body2" color="text.secondary">
              device: <strong>{deviceId}</strong>
            </Typography>
            {edgeIp ? (
              <Typography variant="body2" color="text.secondary">
                • edge ip: <strong>{edgeIp}</strong>
              </Typography>
            ) : null}
          </Stack>

          {updatePollNote && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {updatePollNote}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Progress: {updateProgress.done}/{updateProgress.total} steps ({updateProgress.percent}%)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              End-to-end (browser): {formatDuration(updateClientElapsedMs)} • Server duration: {formatDuration(updateServerDurationMs)}
            </Typography>
            <LinearProgress
              variant={isUpdateRunning ? 'indeterminate' : 'determinate'}
              value={isUpdateRunning ? undefined : updateProgress.percent}
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Steps
          </Typography>
          {updateProgress.steps.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No steps reported yet.
            </Typography>
          ) : (
            <List dense>
              {updateProgress.steps.map((s: any, idx: number) => (
                <ListItem key={`${idx}:${s.step || 'step'}`} alignItems="flex-start">
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {s?.rc === 0 ? <CheckCircleIcon fontSize="small" color="success" /> : <ErrorIcon fontSize="small" color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={`${s.step || 'step'} (rc=${String(s.rc)})`}
                    secondary={
                      (s.out ? `out: ${String(s.out)}` : '') +
                      (s.err ? `${s.out ? '\n' : ''}err: ${String(s.err)}` : '')
                    }
                    secondaryTypographyProps={{ sx: { whiteSpace: 'pre-line', wordBreak: 'break-word' } }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Raw update status (JSON)
            </Typography>
            <TextField fullWidth multiline minRows={8} value={pretty(updateStatus)} InputProps={{ readOnly: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                setUpdatePollNote('Refreshing update status…');
                await pollUpdateOnce();
                setUpdatePollNote(null);
              } catch {
                setUpdatePollNote('Unable to fetch update status (edge may be restarting).');
              }
            }}
            disabled={loading}
          >
            Refresh status
          </Button>
          <Button onClick={openUpdateLogs} variant="outlined">
            View full logs
          </Button>
          {isUpdateRunning && (
            <Button onClick={cancelUpdate} startIcon={<CancelIcon />} color="error" variant="outlined">
              Cancel
            </Button>
          )}
          {!isUpdateRunning && String(updateStatus?.status || '').toLowerCase() === 'error' && (
            <>
              <Button onClick={rollbackNative} color="warning" variant="outlined">
                Rollback native
              </Button>
              <Button onClick={rollbackDocker} color="warning" variant="outlined">
                Rollback docker
              </Button>
            </>
          )}
          <Button onClick={() => setUpdateDialogOpen(false)} disabled={isUpdateRunning}>
            Close
          </Button>
          {isUpdateRunning && (
            <Button onClick={() => setUpdateDialogOpen(false)} color="warning">
              Close anyway
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};
